/**
 * debugLogger
 * Servicio centralizado de logging para soporte técnico.
 * Captura console logs y llamadas API cuando el modo debug está activo.
 */

// Límite de logs en memoria (para no consumir demasiada RAM)
const MAX_LOGS = 500;
const MAX_API_LOGS = 200;

// Almacenamiento en memoria
let consoleLogs = [];
let apiLogs = [];
let isCapturing = false;
let listeners = new Set();

// Referencias a los métodos originales de console
const originalConsole = {
  log: console.log.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  info: console.info.bind(console),
};

/**
 * Notificar a los listeners cuando hay nuevos logs
 */
const notifyListeners = () => {
  listeners.forEach((callback) => {
    try {
      callback({ consoleLogs: [...consoleLogs], apiLogs: [...apiLogs] });
    } catch (e) {
      originalConsole.error("[debugLogger] Error notifying listener:", e);
    }
  });
};

/**
 * Formatear argumentos para almacenamiento
 */
const formatArgs = (args) => {
  return args.map((arg) => {
    if (arg === null) return "null";
    if (arg === undefined) return "undefined";
    if (typeof arg === "object") {
      try {
        return JSON.stringify(arg, null, 2);
      } catch {
        return String(arg);
      }
    }
    return String(arg);
  });
};

/**
 * Agregar log a la memoria
 */
const addConsoleLog = (level, args) => {
  const logEntry = {
    id: Date.now() + Math.random(),
    timestamp: new Date().toISOString(),
    level,
    args: formatArgs(args),
    raw: args,
  };

  consoleLogs.push(logEntry);

  // Mantener límite
  if (consoleLogs.length > MAX_LOGS) {
    consoleLogs = consoleLogs.slice(-MAX_LOGS);
  }

  notifyListeners();
};

/**
 * Agregar log de API a la memoria
 */
const addApiLog = (entry) => {
  const logEntry = {
    id: Date.now() + Math.random(),
    timestamp: new Date().toISOString(),
    ...entry,
  };

  apiLogs.push(logEntry);

  // Mantener límite
  if (apiLogs.length > MAX_API_LOGS) {
    apiLogs = apiLogs.slice(-MAX_API_LOGS);
  }

  notifyListeners();
};

/**
 * Interceptar console methods
 */
const interceptConsole = () => {
  console.log = (...args) => {
    if (isCapturing) {
      addConsoleLog("log", args);
    }
    originalConsole.log(...args);
  };

  console.error = (...args) => {
    if (isCapturing) {
      addConsoleLog("error", args);
    }
    originalConsole.error(...args);
  };

  console.warn = (...args) => {
    if (isCapturing) {
      addConsoleLog("warn", args);
    }
    originalConsole.warn(...args);
  };

  console.info = (...args) => {
    if (isCapturing) {
      addConsoleLog("info", args);
    }
    originalConsole.info(...args);
  };
};

/**
 * Restaurar console methods originales
 */
const restoreConsole = () => {
  console.log = originalConsole.log;
  console.error = originalConsole.error;
  console.warn = originalConsole.warn;
  console.info = originalConsole.info;
};

/**
 * Crear interceptor para axios
 * Debe ser llamado con la instancia de axios a interceptar
 */
const createAxiosInterceptor = (axiosInstance) => {
  // Request interceptor
  const requestInterceptor = axiosInstance.interceptors.request.use(
    (config) => {
      if (isCapturing) {
        // Guardar timestamp para calcular duración
        config._debugStartTime = Date.now();
        config._debugRequestId = Date.now() + Math.random();

        addApiLog({
          type: "request",
          requestId: config._debugRequestId,
          method: config.method?.toUpperCase(),
          url: config.url,
          baseURL: config.baseURL,
          fullUrl: `${config.baseURL || ""}${config.url}`,
          headers: config.headers,
          data: config.data,
          params: config.params,
        });
      }
      return config;
    },
    (error) => {
      if (isCapturing) {
        addApiLog({
          type: "request_error",
          error: error.message,
        });
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor
  const responseInterceptor = axiosInstance.interceptors.response.use(
    (response) => {
      if (isCapturing) {
        const duration = response.config._debugStartTime
          ? Date.now() - response.config._debugStartTime
          : null;

        addApiLog({
          type: "response",
          requestId: response.config._debugRequestId,
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          fullUrl: `${response.config.baseURL || ""}${response.config.url}`,
          status: response.status,
          statusText: response.statusText,
          data: response.data,
          duration: duration ? `${duration}ms` : "N/A",
        });
      }
      return response;
    },
    (error) => {
      if (isCapturing) {
        const duration = error.config?._debugStartTime
          ? Date.now() - error.config._debugStartTime
          : null;

        addApiLog({
          type: "response_error",
          requestId: error.config?._debugRequestId,
          method: error.config?.method?.toUpperCase(),
          url: error.config?.url,
          fullUrl: `${error.config?.baseURL || ""}${error.config?.url || ""}`,
          status: error.response?.status,
          statusText: error.response?.statusText,
          errorMessage: error.message,
          responseData: error.response?.data,
          duration: duration ? `${duration}ms` : "N/A",
        });
      }
      return Promise.reject(error);
    }
  );

  // Retornar función para remover interceptors
  return () => {
    axiosInstance.interceptors.request.eject(requestInterceptor);
    axiosInstance.interceptors.response.eject(responseInterceptor);
  };
};

/**
 * API pública del debugLogger
 */
const debugLogger = {
  /**
   * Iniciar captura de logs
   */
  startCapture: () => {
    if (!isCapturing) {
      isCapturing = true;
      interceptConsole();
      originalConsole.info("[debugLogger] Captura iniciada");
    }
  },

  /**
   * Detener captura de logs
   */
  stopCapture: () => {
    if (isCapturing) {
      isCapturing = false;
      restoreConsole();
      originalConsole.info("[debugLogger] Captura detenida");
    }
  },

  /**
   * Verificar si está capturando
   */
  isCapturing: () => isCapturing,

  /**
   * Obtener todos los console logs
   */
  getConsoleLogs: () => [...consoleLogs],

  /**
   * Obtener todos los API logs
   */
  getApiLogs: () => [...apiLogs],

  /**
   * Obtener logs filtrados por nivel
   */
  getLogsByLevel: (level) => consoleLogs.filter((log) => log.level === level),

  /**
   * Limpiar todos los logs
   */
  clearLogs: () => {
    consoleLogs = [];
    apiLogs = [];
    notifyListeners();
    originalConsole.info("[debugLogger] Logs limpiados");
  },

  /**
   * Limpiar solo console logs
   */
  clearConsoleLogs: () => {
    consoleLogs = [];
    notifyListeners();
  },

  /**
   * Limpiar solo API logs
   */
  clearApiLogs: () => {
    apiLogs = [];
    notifyListeners();
  },

  /**
   * Suscribirse a cambios en los logs
   * @param {Function} callback - Función a llamar cuando hay cambios
   * @returns {Function} - Función para desuscribirse
   */
  subscribe: (callback) => {
    listeners.add(callback);
    return () => listeners.delete(callback);
  },

  /**
   * Crear interceptor de axios
   * @param {AxiosInstance} axiosInstance - Instancia de axios a interceptar
   * @returns {Function} - Función para remover interceptors
   */
  interceptAxios: createAxiosInterceptor,

  /**
   * Log manual (útil para puntos específicos del código)
   */
  log: (category, message, data = null) => {
    if (isCapturing) {
      addConsoleLog("log", [`[${category}]`, message, data].filter(Boolean));
    }
  },

  /**
   * Log de API manual (para llamadas que no usan axios interceptado)
   */
  logApi: (entry) => {
    if (isCapturing) {
      addApiLog(entry);
    }
  },

  /**
   * Exportar logs a JSON (para descargar)
   */
  exportLogs: () => {
    return JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        consoleLogs,
        apiLogs,
      },
      null,
      2
    );
  },
};

export default debugLogger;
